import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ClassifierService } from '@cl/core/services';
import { GroupPermissionsType } from '@cl/shared/group-permissions-type.enum';
import { Classifier, ClassifierType } from '@cl/shared/models';
import { Invoice } from '@cl/shared/models/invoices/invoice.model';
import { Sinvoice } from '@cl/shared/models/sinvoices/sinvoice.model';
import { SumPipe } from '@cl/shared/pipes/sum.pipe';
import { NzMessageService } from 'ng-zorro-antd/message';
import { debounceTime, distinctUntilChanged, merge, Observable, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'cl-invoice-sum-fields',
    standalone: false,
    templateUrl: './invoice-sum-fields.component.html',
    styleUrl: './invoice-sum-fields.component.less',
})
export class InvoiceSumFieldsComponent implements OnInit, OnDestroy {
    GroupPermission = GroupPermissionsType;

    @Input() objectForm: UntypedFormGroup;
    @Input() invoice: Invoice | Sinvoice;

    isAutomaticCalculationsEnabled: boolean = true;
    sumWithVatBeforePenalty: number;

    vatTypes$: Observable<Classifier[]>;

    sumFormatter = (value: number): string => {
        return `${new SumPipe().transform(value)}`;
    };

    private destroy$: Subject<void> = new Subject<void>();

    constructor(private classifiersService: ClassifierService, private messageService: NzMessageService, private cdr: ChangeDetectorRef) {}

    async ngOnInit(): Promise<void> {
        this.vatTypes$ = this.classifiersService.getClassifiers(ClassifierType.Vat);

        this.setupSumUpdateChecks();
        if (this.invoice) {
            this.sumWithVatBeforePenalty = Number((this.invoice.sumWithoutVat + (this.invoice.sumVat ?? 0)).toFixed(2));
        }
    }

    onIsAutomaticCalculationsEnabledChanged(isAutomaticCalculationsEnabled: boolean): void {
        if (!isAutomaticCalculationsEnabled) {
            return;
        }

        this.recalculateAllSums();
    }

    recalculateAllSums(): void {
        if (!this.isAutomaticCalculationsEnabled) {
            return;
        }

        const sumWithoutVat: number = this.objectForm.controls['sumWithoutVat'].value ?? 0;
        const vatPercent: number = this.objectForm.controls['vatPercent'].value ?? 0;
        const penaltySumWithoutVat: number = this.objectForm.controls['penaltySumWithoutVat'].value ?? 0;

        const sumVat: number = Number((sumWithoutVat * (vatPercent / 100)).toFixed(2));
        const sumWithVat: number = Number((sumWithoutVat + sumVat + penaltySumWithoutVat).toFixed(2));

        this.objectForm.controls['sumVat'].setValue(sumVat, { emitEvent: false });
        this.objectForm.controls['sumWithVat'].setValue(sumWithVat, { emitEvent: false });
        this.sumWithVatBeforePenalty = Number((sumWithoutVat + sumVat).toFixed(2));
    }

    setupSumUpdateChecks(): void {
        // trigger recalculation of sums, if sumWithoutVat, vatPercent or penaltySumWithoutVat values are changed.
        // general recalculation method used here
        merge(
            this.objectForm.controls['sumWithoutVat'].valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)),
            this.objectForm.controls['vatPercent'].valueChanges.pipe(takeUntil(this.destroy$)),
            this.objectForm.controls['penaltySumWithoutVat'].valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)),
        ).subscribe(() => {
            if (!this.isAutomaticCalculationsEnabled) {
                return;
            }

            this.recalculateAllSums();
        });

        // do the specific calculations, when sumVat value changed
        this.objectForm.controls['sumVat'].valueChanges
            .pipe((debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)))
            .subscribe((sumVat: number) => {
                if (!this.isAutomaticCalculationsEnabled) {
                    return;
                }

                const vatPercent: number = this.objectForm.controls['vatPercent'].value ?? 0;
                let sumWithoutVat: number = this.objectForm.controls['sumWithoutVat'].value ?? 0;
                const penaltySumWithoutVat: number = this.objectForm.controls['penaltySumWithoutVat'].value ?? 0;

                if (vatPercent == 0) {
                    this.messageService.warning('Izvēlētā PVN procentu likme ir 0%, PVN summa nevar būt lielāka par 0 EUR!');
                    this.objectForm.controls['sumVat'].setValue(0, { emitEvent: false });
                    this.objectForm.controls['sumWithVat'].setValue(sumWithoutVat + penaltySumWithoutVat, { emitEvent: false });
                    return;
                }

                sumWithoutVat = Number(((sumVat * 100) / vatPercent).toFixed(2));
                const sumWithVat = Number((sumWithoutVat + sumVat + penaltySumWithoutVat).toFixed(2));

                this.sumWithVatBeforePenalty = Number((sumWithoutVat + sumVat).toFixed(2));
                this.objectForm.controls['sumWithoutVat'].setValue(sumWithoutVat, { emitEvent: false });
                this.objectForm.controls['sumWithVat'].setValue(sumWithVat, { emitEvent: false });
            });

        // do the specific calculations, when sumWithVat value changed
        this.objectForm.controls['sumWithVat'].valueChanges
            .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe((sumWithVat: number): void => {
                if (!this.isAutomaticCalculationsEnabled) {
                    return;
                }

                const vatPercent: number = this.objectForm.controls['vatPercent'].value ?? 0;
                const penaltySumWithoutVat: number = this.objectForm.controls['penaltySumWithoutVat'].value ?? 0;

                if (vatPercent == 0) {
                    this.sumWithVatBeforePenalty = Number((sumWithVat - penaltySumWithoutVat).toFixed(2));
                    this.objectForm.controls['sumWithoutVat'].setValue(this.sumWithVatBeforePenalty, { emitEvent: false });
                    this.objectForm.controls['sumVat'].setValue(0, { emitEvent: false });
                    return;
                }

                const sumWithoutVat: number = Number(((sumWithVat - penaltySumWithoutVat) / (1 + vatPercent / 100)).toFixed(2));
                const sumVat: number = Number((sumWithVat - penaltySumWithoutVat - sumWithoutVat).toFixed(2));
                this.sumWithVatBeforePenalty = sumWithoutVat + sumVat;

                this.objectForm.controls['sumVat'].setValue(sumVat, { emitEvent: false });
                this.objectForm.controls['sumWithoutVat'].setValue(sumWithoutVat, { emitEvent: false });
            });
    }

    stringToNumber(numberString: string): number {
        return parseInt(numberString, 10);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
